import type { SupportedLogSourceModel } from '../SourceInfoBlock/SourceInfoBlock.types';
import type {
  CloudSource,
  DataSourceModel,
  FlattenedSource,
  SupportedLogSource
} from './SourceSelectionStep.types';

/**
 * Transforms Azure Event Hub API response into standard SupportedLogSource format.
 *
 * Business Context: Azure Event Hub sources are fetched dynamically from a separate API
 * endpoint and need to be normalized to match the format used by other source types.
 *
 * @param sources - Raw API response from Azure Event Hub endpoint
 * @returns Normalized log sources with consistent label/name structure
 */
export const mapAzureEventHubSources = (
  sources: SupportedLogSourceModel[]
): SupportedLogSource[] => {
  return sources.map((source) => ({
    label: source.display_name,
    name: source.display_name
  }));
};

/**
 * Extracts supported log sources from nested data_sources structure.
 *
 * Business Context: Source types have two formats:
 * 1. Simple sources (e.g., CiscoUmbrella, AWS CloudTrail) - single data source
 * 2. Complex sources (e.g., Microsoft Entra ID) - parent with multiple child providers
 *
 * For complex sources, we create hierarchical labels like:
 * - "Microsoft Entra ID - Graph API"
 * - "Microsoft Entra ID - O365 API"
 *
 * Names use dot notation for backend identification: "parent.child"
 *
 * Why flatMap: Transforms potentially nested array structure into a flat list
 * suitable for display in the source selection table.
 *
 * @param dataSources - Nested data source structure from source type definition
 * @returns Flat list of selectable log sources with display labels
 */
export const extractLogSourcesFromDataSources = (
  dataSources: DataSourceModel[] | undefined
): SupportedLogSource[] => {
  if (!dataSources) return [];

  return dataSources.flatMap((dataSource) => {
    // Complex source with multiple child providers (e.g., Microsoft Entra ID)
    if (dataSource.data_sources?.length) {
      const parentLabel =
        dataSource.data_source_label || dataSource.data_source;

      return dataSource.data_sources.map((childDataSource) => {
        const childLabel =
          childDataSource.data_source_label || childDataSource.data_source;

        return {
          // Display: "Microsoft Entra ID - Graph API"
          label: `${parentLabel} - ${childLabel}`,
          // Backend identifier: "microsoft_entra_id.graph_api"
          name: `${dataSource.data_source}.${childDataSource.data_source}`
        };
      });
    }

    // Simple source without children (e.g., CiscoUmbrella)
    const label = dataSource.data_source_label || dataSource.data_source;
    return label ? [{ label, name: dataSource.data_source }] : [];
  });
};

/**
 * Builds a map of display name -> vendor for efficient O(1) lookups.
 *
 * Why this optimization: Without pre-building this map, we'd need nested loops
 * to find the vendor for each child source (O(n²) complexity). With potentially
 * hundreds of sources, this would cause noticeable UI lag. The map reduces
 * lookup time from O(n) to O(1).
 *
 * Business Rule: Vendor inheritance follows this priority:
 * 1. Child's explicit vendor (if specified)
 * 2. Parent data source's vendor (if specified)
 * 3. Source type's vendor (fallback)
 *
 * Example: "Microsoft Entra ID - Graph API" might have:
 * - Child vendor: "Microsoft" (specific)
 * - Parent vendor: "Microsoft" (fallback)
 * - Source vendor: "Microsoft" (ultimate fallback)
 *
 * @param source - Cloud source with potential nested data sources
 * @returns Map of display name to vendor for O(1) lookups during flattening
 */
export const buildVendorMap = (source: CloudSource): Map<string, string> => {
  const vendorMap = new Map<string, string>();

  if (!source.data_sources) {
    return vendorMap;
  }

  source.data_sources.forEach((dataSource) => {
    const dataSourceLabel =
      dataSource.data_source_label || dataSource.data_source;
    const dataSourceVendor = dataSource.vendor || source.vendor;

    // Map parent data source label to its vendor
    vendorMap.set(dataSourceLabel, dataSourceVendor);

    // Map nested children with their specific vendors
    if (dataSource.data_sources?.length) {
      dataSource.data_sources.forEach((childDataSource) => {
        const childLabel =
          childDataSource.data_source_label || childDataSource.data_source;
        const fullChildLabel = `${dataSourceLabel} - ${childLabel}`;
        // Child can override parent vendor, otherwise inherits from parent
        const childVendor = childDataSource.vendor || dataSourceVendor;

        vendorMap.set(fullChildLabel, childVendor);
      });
    }
  });

  return vendorMap;
};

/**
 * Flattens cloud sources by expanding parents with children into separate rows.
 *
 * Business Context: Sources come in two forms:
 * 1. Simple sources → Single selectable row (e.g., "AWS CloudTrail")
 * 2. Parent sources → Multiple selectable rows, one per child provider
 *    (e.g., "Microsoft Entra ID" expands to "Graph API", "O365 API" rows)
 *
 * Why flatten: The UI table needs one row per selectable option. Parent sources
 * with multiple providers can't be selected directly - users must choose a
 * specific provider. Flattening transforms the hierarchical structure into a
 * flat list suitable for table rendering.
 *
 * Performance: Uses pre-built vendor map for O(1) lookups instead of
 * searching nested structures repeatedly (O(n²) → O(n)).
 *
 * @param sources - Cloud sources with potential nested child providers
 * @returns Flat list where each item represents one selectable table row
 */
export const flattenCloudSources = (
  sources: CloudSource[]
): FlattenedSource[] => {
  return sources.flatMap((source): FlattenedSource[] => {
    // Simple source without child providers - return as single row
    if (!source.supported_log_sources?.length) {
      return [
        {
          source,
          displayName: source.product,
          isChild: false,
          childName: undefined,
          vendorOverride: undefined
        }
      ];
    }

    // Parent source with children - expand into multiple rows
    // Build vendor map once for O(1) lookups across all children
    const vendorMap = buildVendorMap(source);

    // Create one row per child provider
    return source.supported_log_sources
      .filter((childSource) => childSource.label && childSource.name)
      .map((childSource) => ({
        source,
        displayName: childSource.label!,
        isChild: true,
        childName: childSource.name!,
        // Look up vendor from map, fallback to source vendor if not found
        vendorOverride: vendorMap.get(childSource.label!) || source.vendor
      }));
  });
};

/**
 * Sorts flattened sources for consistent, predictable table ordering.
 *
 * Sort Priority:
 * 1. Primary: Vendor (alphabetically) - groups sources by vendor
 * 2. Secondary: Display name (alphabetically) - within each vendor group
 *
 * Business Context: Grouping by vendor helps users quickly find sources from
 * a specific provider (e.g., all Microsoft sources together, all AWS together).
 *
 * Why localeCompare: Provides locale-aware string comparison that handles
 * special characters and international text correctly.
 *
 * @param sources - Flattened source list to sort
 * @returns Sorted list with consistent vendor grouping and alphabetical ordering
 */
export const sortFlattenedSources = (
  sources: FlattenedSource[]
): FlattenedSource[] => {
  return sources.sort((sourceA, sourceB) => {
    const vendorA = sourceA.vendorOverride || sourceA.source.vendor;
    const vendorB = sourceB.vendorOverride || sourceB.source.vendor;

    // Primary sort: by vendor
    if (vendorA !== vendorB) {
      return vendorA.localeCompare(vendorB);
    }

    // Secondary sort: by display name within same vendor
    return sourceA.displayName.localeCompare(sourceB.displayName);
  });
};
